import moment from 'moment';
import HelperMixin from '../../Shared/HelperMixin.vue';
import PaginationHelper from '../../Shared/PaginationHelper.vue';

const TABS = [
    { key: 'current', label: 'المخزون الحالي', icon: 'ti ti-box' },
    { key: 'expired', label: 'الأصناف المنتهية', icon: 'ti ti-alert-triangle' },
    { key: 'expiringSoon', label: 'قريبة الانتهاء', icon: 'ti ti-clock' },
    { key: 'lowStock', label: 'منخفضة المخزون', icon: 'ti ti-package' },
    { key: 'byWarehouse', label: 'حسب المخزن', icon: 'ti ti-building-warehouse' },
    { key: 'byCategory', label: 'حسب التصنيف', icon: 'ti ti-categories' },
    { key: 'lastInventory', label: 'تقرير الجرد الأخير', icon: 'ti ti-clipboard-check' },
    { key: 'charts', label: 'لوحة المؤشرات', icon: 'ti ti-chart-bar' }
];

const PAGE_SIZES = [10, 25, 50, 100];

const SORT_COLUMNS_CURRENT = [
    { key: 'productName', label: 'المنتج' },
    { key: 'scientificName', label: 'الصنف النمطي' },
    { key: 'categoryName', label: 'التصنيف' },
    { key: 'batchNumber', label: 'التشغيلة' },
    { key: 'expiryDate', label: 'تاريخ الصلاحية' },
    { key: 'warehouseName', label: 'المخزن' },
    { key: 'quantity', label: 'الكمية' },
    { key: 'unitPrice', label: 'سعر الوحدة' },
    { key: 'totalValue', label: 'القيمة الإجمالية' }
];

const STATUS_COLORS = { ok: '#28C76F', near: '#FF9F43', expired: '#EA5455' };

function defaultFilters() {
    return {
        branchId: '',
        stockingId: '',
        categoryId: '',
        manufacturerId: '',
        countryId: '',
        expiryFrom: '',
        expiryTo: '',
        search: ''
    };
}

export default {
    name: 'StockReports',
    mixins: [HelperMixin],
    components: { PaginationHelper },

    async created() {
        await this.CheckLoginStatus();
        if (!this.canViewPage) {
            this.$helper.ShowMessage('error', 'غير مصرح', 'ليس لديك صلاحية للوصول إلى تقارير المخزون');
            this.href('/Home');
            return;
        }

        await this.loadReferenceData();
        await this.refresh();
    },

    beforeDestroy() {
        this.destroyCharts();
    },

    computed: {
        tabs() { return TABS; },
        pageSizesOptions() { return PAGE_SIZES; },
        sortColumnsCurrent() { return SORT_COLUMNS_CURRENT; },

        canViewPage() {
            return this.canViewStockReportsUi();
        },

        canExportPage() {
            return this.canExportStockReportsUi();
        },

        canSelectBranch() {
            return this.hasInventoryAndStockAccessUi() || this.isMinistryPharmacyDepartmentRole();
        },

        metricsCards() {
            const m = this.metrics || {};
            return [
                {
                    title: 'إجمالي قيمة المخزون',
                    value: this.fmtCurrency(m.totalValue),
                    tone: 'primary',
                    icon: 'ti ti-coin'
                },
                {
                    title: 'الأصناف المنتهية',
                    value: this.fmtNumber(m.expiredCount),
                    tone: 'danger',
                    icon: 'ti ti-alert-triangle'
                },
                {
                    title: 'قريبة الانتهاء (60 يوم)',
                    value: this.fmtNumber(m.nearExpiryCount),
                    tone: 'warning',
                    icon: 'ti ti-clock'
                },
                {
                    title: 'منخفضة المخزون',
                    value: this.fmtNumber(m.lowStockCount),
                    tone: 'info',
                    icon: 'ti ti-package'
                }
            ];
        },

        activeTabHasTable() {
            return ['current', 'expired', 'expiringSoon', 'lowStock', 'byWarehouse', 'byCategory'].includes(this.activeTab);
        },

        activeTabExportable() {
            return [
                'current', 'expired', 'expiringSoon', 'lowStock', 'byWarehouse', 'byCategory'
            ].includes(this.activeTab);
        },

        // فلاتر مرسلة إلى الـ API (تحوي القيم النصية كأرقام عند الإمكان).
        apiFilters() {
            const f = this.filters || {};
            return {
                branchId: f.branchId || undefined,
                stockingId: f.stockingId || undefined,
                categoryId: f.categoryId || undefined,
                manufacturerId: f.manufacturerId || undefined,
                countryId: f.countryId || undefined,
                expiryFrom: f.expiryFrom || undefined,
                expiryTo: f.expiryTo || undefined,
                search: (f.search || '').trim() || undefined,
                windowDays: this.expiringWindowDays || undefined
            };
        }
    },

    data() {
        return {
            activeTab: 'current',
            ScreenTitle: 'تقارير المخزون',

            filters: defaultFilters(),
            expiringWindowDays: 60,

            branchesList: [],
            stockingsList: [],
            categoriesList: [],
            manufacturersList: [],
            countriesList: [],

            metrics: { totalValue: 0, expiredCount: 0, nearExpiryCount: 0, lowStockCount: 0 },
            metricsLoading: false,

            // Current Stock tab
            currentRows: [],
            currentTotal: 0,
            currentPage: 1,
            currentPageSize: 25,
            currentSortBy: 'expiryDate',
            currentSortDir: 'asc',
            currentLoading: false,

            // Expired tab
            expiredRows: [],
            expiredTotal: 0,
            expiredPage: 1,
            expiredPageSize: 25,
            expiredLoading: false,

            // ExpiringSoon tab
            expiringRows: [],
            expiringTotal: 0,
            expiringPage: 1,
            expiringPageSize: 25,
            expiringLoading: false,

            // LowStock tab
            lowStockRows: [],
            lowStockTotal: 0,
            lowStockPage: 1,
            lowStockPageSize: 25,
            lowStockLoading: false,

            // ByWarehouse tab
            byWarehouseRows: [],
            byWarehouseLoading: false,

            // ByCategory tab
            byCategoryRows: [],
            byCategoryLoading: false,

            // LastInventory tab
            lastInventorySession: null,
            lastInventoryLines: [],
            lastInventoryFound: false,
            lastInventoryLoading: false,

            // Charts tab
            chartsData: null,
            chartsLoading: false,
            chartInstances: {}
        };
    },

    methods: {
        // ─── Helpers ─────────────────────────────────────────────────
        fmtNumber(v) {
            if (v === null || v === undefined || v === '') return '0';
            const n = Number(v);
            if (!Number.isFinite(n)) return '0';
            return n.toLocaleString('ar-LY');
        },
        fmtCurrency(v) {
            const n = Number(v || 0);
            if (!Number.isFinite(n)) return '0';
            return n.toLocaleString('ar-LY', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
        },
        fmtDate(d) {
            if (!d) return '—';
            try { return moment(d).format('DD/MM/YYYY'); } catch { return d; }
        },

        rowIndex(page, pageSize, index) {
            const p = Number(page || 1);
            const s = Number(pageSize || 25);
            return ((p - 1) * s) + index + 1;
        },

        statusClass(status) {
            if (status === 'expired') return 'stock-status-chip stock-status-chip--expired';
            if (status === 'near') return 'stock-status-chip stock-status-chip--near';
            return 'stock-status-chip stock-status-chip--ok';
        },

        // ─── Reference data ──────────────────────────────────────────
        async loadReferenceData() {
            try {
                const ops = [
                    this.$http.GetAllBranches().then(r => { this.branchesList = (r.data && r.data.info) || []; }).catch(() => { this.branchesList = []; }),
                    this.$http.GetAllStocking().then(r => { this.stockingsList = (r.data && r.data.info) || []; }).catch(() => { this.stockingsList = []; }),
                    this.$http.GetAllCategories().then(r => { this.categoriesList = (r.data && r.data.info) || []; }).catch(() => { this.categoriesList = []; }),
                    this.$http.GetAllManufacturers().then(r => { this.manufacturersList = (r.data && r.data.info) || []; }).catch(() => { this.manufacturersList = []; }),
                    this.$http.GetAllCountries().then(r => { this.countriesList = (r.data && r.data.info) || []; }).catch(() => { this.countriesList = []; })
                ];
                await Promise.all(ops);
            } catch (_) {
                // ignored: قيم افتراضية فارغة
            }
        },

        // ─── Filters & Refresh ───────────────────────────────────────
        applyFilters() {
            // إعادة الترقيم لأول صفحة في كل التبويبات.
            this.currentPage = 1;
            this.expiredPage = 1;
            this.expiringPage = 1;
            this.lowStockPage = 1;
            this.refresh();
        },

        resetFilters() {
            this.filters = defaultFilters();
            this.expiringWindowDays = 60;
            this.applyFilters();
        },

        async refresh() {
            await Promise.all([this.loadMetrics(), this.loadActiveTab()]);
        },

        switchTab(tabKey) {
            if (!tabKey || tabKey === this.activeTab) return;
            this.activeTab = tabKey;
            this.$nextTick(() => this.loadActiveTab());
        },

        loadActiveTab() {
            switch (this.activeTab) {
                case 'current': return this.loadCurrent();
                case 'expired': return this.loadExpired();
                case 'expiringSoon': return this.loadExpiringSoon();
                case 'lowStock': return this.loadLowStock();
                case 'byWarehouse': return this.loadByWarehouse();
                case 'byCategory': return this.loadByCategory();
                case 'lastInventory': return this.loadLastInventory();
                case 'charts': return this.loadCharts();
                default: return Promise.resolve();
            }
        },

        // ─── Metrics ─────────────────────────────────────────────────
        async loadMetrics() {
            this.metricsLoading = true;
            try {
                const res = await this.$http.GetStockReportMetrics(this.apiFilters);
                this.metrics = res.data || {};
            } catch (_) {
                this.metrics = { totalValue: 0, expiredCount: 0, nearExpiryCount: 0, lowStockCount: 0 };
            } finally {
                this.metricsLoading = false;
            }
        },

        // ─── Current Stock ───────────────────────────────────────────
        async loadCurrent(page) {
            this.currentPage = page || this.currentPage || 1;
            this.currentLoading = true;
            try {
                const res = await this.$http.GetStockReportCurrent({
                    ...this.apiFilters,
                    pageNo: this.currentPage,
                    pageSize: this.currentPageSize,
                    sortBy: this.currentSortBy,
                    sortDir: this.currentSortDir
                });
                this.currentRows = (res.data && res.data.info) || [];
                this.currentTotal = (res.data && res.data.count) || 0;
            } catch (_) {
                this.currentRows = [];
                this.currentTotal = 0;
            } finally {
                this.currentLoading = false;
            }
        },

        toggleCurrentSort(column) {
            if (!column) return;
            if (this.currentSortBy === column) {
                this.currentSortDir = this.currentSortDir === 'asc' ? 'desc' : 'asc';
            } else {
                this.currentSortBy = column;
                this.currentSortDir = 'asc';
            }
            this.loadCurrent(1);
        },

        currentSortIndicator(column) {
            if (this.currentSortBy !== column) return '';
            return this.currentSortDir === 'asc' ? '↑' : '↓';
        },

        // ─── Expired ─────────────────────────────────────────────────
        async loadExpired(page) {
            this.expiredPage = page || this.expiredPage || 1;
            this.expiredLoading = true;
            try {
                const res = await this.$http.GetStockReportExpired({
                    ...this.apiFilters,
                    pageNo: this.expiredPage,
                    pageSize: this.expiredPageSize
                });
                this.expiredRows = (res.data && res.data.info) || [];
                this.expiredTotal = (res.data && res.data.count) || 0;
            } catch (_) {
                this.expiredRows = [];
                this.expiredTotal = 0;
            } finally {
                this.expiredLoading = false;
            }
        },

        // ─── ExpiringSoon ────────────────────────────────────────────
        async loadExpiringSoon(page) {
            this.expiringPage = page || this.expiringPage || 1;
            this.expiringLoading = true;
            try {
                const res = await this.$http.GetStockReportExpiringSoon({
                    ...this.apiFilters,
                    pageNo: this.expiringPage,
                    pageSize: this.expiringPageSize
                });
                this.expiringRows = (res.data && res.data.info) || [];
                this.expiringTotal = (res.data && res.data.count) || 0;
            } catch (_) {
                this.expiringRows = [];
                this.expiringTotal = 0;
            } finally {
                this.expiringLoading = false;
            }
        },

        // ─── Low stock ───────────────────────────────────────────────
        async loadLowStock(page) {
            this.lowStockPage = page || this.lowStockPage || 1;
            this.lowStockLoading = true;
            try {
                const res = await this.$http.GetStockReportLowStock({
                    ...this.apiFilters,
                    pageNo: this.lowStockPage,
                    pageSize: this.lowStockPageSize
                });
                this.lowStockRows = (res.data && res.data.info) || [];
                this.lowStockTotal = (res.data && res.data.count) || 0;
            } catch (_) {
                this.lowStockRows = [];
                this.lowStockTotal = 0;
            } finally {
                this.lowStockLoading = false;
            }
        },

        // ─── ByWarehouse / ByCategory ────────────────────────────────
        async loadByWarehouse() {
            this.byWarehouseLoading = true;
            try {
                const res = await this.$http.GetStockReportByWarehouse(this.apiFilters);
                this.byWarehouseRows = (res.data && res.data.info) || [];
                this.$nextTick(() => this.renderByWarehouseCharts());
            } catch (_) {
                this.byWarehouseRows = [];
            } finally {
                this.byWarehouseLoading = false;
            }
        },

        async loadByCategory() {
            this.byCategoryLoading = true;
            try {
                const res = await this.$http.GetStockReportByCategory(this.apiFilters);
                this.byCategoryRows = (res.data && res.data.info) || [];
                this.$nextTick(() => this.renderByCategoryCharts());
            } catch (_) {
                this.byCategoryRows = [];
            } finally {
                this.byCategoryLoading = false;
            }
        },

        // ─── LastInventory ───────────────────────────────────────────
        async loadLastInventory() {
            this.lastInventoryLoading = true;
            try {
                const res = await this.$http.GetStockReportLastInventory({
                    branchId: this.filters.branchId || undefined,
                    stockingId: this.filters.stockingId || undefined
                });
                const data = res.data || {};
                this.lastInventoryFound = !!data.found;
                this.lastInventorySession = data.session || null;
                this.lastInventoryLines = data.lines || [];
            } catch (_) {
                this.lastInventoryFound = false;
                this.lastInventorySession = null;
                this.lastInventoryLines = [];
            } finally {
                this.lastInventoryLoading = false;
            }
        },

        // ─── Charts tab ──────────────────────────────────────────────
        async loadCharts() {
            this.chartsLoading = true;
            try {
                const res = await this.$http.GetStockReportChartsData(this.apiFilters);
                this.chartsData = res.data || {};
                this.$nextTick(() => this.renderGeneralCharts());
            } catch (_) {
                this.chartsData = null;
            } finally {
                this.chartsLoading = false;
            }
        },

        // ─── Chart helpers ───────────────────────────────────────────
        destroyCharts() {
            Object.keys(this.chartInstances || {}).forEach((k) => {
                try { this.chartInstances[k] && this.chartInstances[k].destroy(); } catch (_) { /* ignore */ }
            });
            this.chartInstances = {};
        },

        registerChart(key, chart) {
            if (this.chartInstances[key]) {
                try { this.chartInstances[key].destroy(); } catch (_) { /* ignore */ }
            }
            this.chartInstances[key] = chart;
        },

        renderByWarehouseCharts() {
            if (typeof window.ApexCharts === 'undefined') return;

            const rows = (this.byWarehouseRows || []).slice(0, 12);
            const labels = rows.map((r) => r.warehouseName || '—');
            const qty = rows.map((r) => Number(r.totalQuantity || 0));
            const val = rows.map((r) => Number(r.totalValue || 0));

            const barEl = document.getElementById('warehouseQuantityChart');
            if (barEl) {
                barEl.innerHTML = '';
                const bar = new window.ApexCharts(barEl, {
                    chart: { type: 'bar', height: 320, toolbar: { show: false } },
                    series: [{ name: 'إجمالي الكمية', data: qty }],
                    xaxis: { categories: labels },
                    colors: ['#7367F0'],
                    plotOptions: { bar: { borderRadius: 4, columnWidth: '55%' } },
                    dataLabels: { enabled: false },
                    grid: { borderColor: 'rgba(115,103,240,0.12)' }
                });
                bar.render();
                this.registerChart('warehouseQuantityChart', bar);
            }

            const pieEl = document.getElementById('warehouseValueChart');
            if (pieEl) {
                pieEl.innerHTML = '';
                const pie = new window.ApexCharts(pieEl, {
                    chart: { type: 'pie', height: 320 },
                    series: val,
                    labels: labels,
                    legend: { position: 'bottom' },
                    dataLabels: { enabled: true, formatter: (v) => `${Math.round(v)}%` }
                });
                pie.render();
                this.registerChart('warehouseValueChart', pie);
            }
        },

        renderByCategoryCharts() {
            if (typeof window.ApexCharts === 'undefined') return;
            const rows = (this.byCategoryRows || []).slice(0, 12);
            const labels = rows.map((r) => r.categoryName || 'غير مصنف');
            const qty = rows.map((r) => Number(r.totalQuantity || 0));
            const val = rows.map((r) => Number(r.totalValue || 0));

            const barEl = document.getElementById('categoryQuantityChart');
            if (barEl) {
                barEl.innerHTML = '';
                const bar = new window.ApexCharts(barEl, {
                    chart: { type: 'bar', height: 320, toolbar: { show: false } },
                    series: [{ name: 'إجمالي الكمية', data: qty }],
                    xaxis: { categories: labels },
                    colors: ['#00CFE8'],
                    plotOptions: { bar: { borderRadius: 4, columnWidth: '55%' } },
                    dataLabels: { enabled: false },
                    grid: { borderColor: 'rgba(0,207,232,0.12)' }
                });
                bar.render();
                this.registerChart('categoryQuantityChart', bar);
            }

            const pieEl = document.getElementById('categoryValueChart');
            if (pieEl) {
                pieEl.innerHTML = '';
                const pie = new window.ApexCharts(pieEl, {
                    chart: { type: 'pie', height: 320 },
                    series: val,
                    labels: labels,
                    legend: { position: 'bottom' },
                    dataLabels: { enabled: true, formatter: (v) => `${Math.round(v)}%` }
                });
                pie.render();
                this.registerChart('categoryValueChart', pie);
            }
        },

        renderGeneralCharts() {
            if (typeof window.ApexCharts === 'undefined' || !this.chartsData) return;

            const d = this.chartsData;
            const months = (d.monthly || []).map((x) => x.month);
            const monthQty = (d.monthly || []).map((x) => Number(x.totalQuantity || 0));
            const monthVal = (d.monthly || []).map((x) => Number(x.totalValue || 0));

            // 1) خط زمني — قيمة المخزون.
            const lineEl = document.getElementById('monthlyValueChart');
            if (lineEl) {
                lineEl.innerHTML = '';
                const line = new window.ApexCharts(lineEl, {
                    chart: { type: 'area', height: 280, toolbar: { show: false } },
                    series: [
                        { name: 'قيمة الإضافات', data: monthVal },
                        { name: 'الكمية المضافة', data: monthQty }
                    ],
                    xaxis: { categories: months },
                    colors: ['#7367F0', '#28C76F'],
                    stroke: { curve: 'smooth', width: 2 },
                    dataLabels: { enabled: false },
                    legend: { position: 'top' },
                    grid: { borderColor: 'rgba(115,103,240,0.1)' }
                });
                line.render();
                this.registerChart('monthlyValueChart', line);
            }

            // 2) أكثر 10 منتجات قيمةً.
            const valEl = document.getElementById('topByValueChart');
            if (valEl) {
                valEl.innerHTML = '';
                const top = (d.topByValue || []);
                const bar = new window.ApexCharts(valEl, {
                    chart: { type: 'bar', height: 320, toolbar: { show: false } },
                    series: [{ name: 'القيمة الإجمالية', data: top.map((x) => Number(x.totalValue || 0)) }],
                    xaxis: { categories: top.map((x) => (x.productName || '').slice(0, 24)) },
                    colors: ['#FF9F43'],
                    plotOptions: { bar: { borderRadius: 4, horizontal: true } },
                    dataLabels: { enabled: false },
                    grid: { borderColor: 'rgba(255,159,67,0.1)' }
                });
                bar.render();
                this.registerChart('topByValueChart', bar);
            }

            // 3) أكثر 10 منتجات كميةً.
            const qtyEl = document.getElementById('topByQuantityChart');
            if (qtyEl) {
                qtyEl.innerHTML = '';
                const top = (d.topByQuantity || []);
                const bar = new window.ApexCharts(qtyEl, {
                    chart: { type: 'bar', height: 320, toolbar: { show: false } },
                    series: [{ name: 'إجمالي الكمية', data: top.map((x) => Number(x.totalQuantity || 0)) }],
                    xaxis: { categories: top.map((x) => (x.productName || '').slice(0, 24)) },
                    colors: ['#00CFE8'],
                    plotOptions: { bar: { borderRadius: 4, horizontal: true } },
                    dataLabels: { enabled: false },
                    grid: { borderColor: 'rgba(0,207,232,0.1)' }
                });
                bar.render();
                this.registerChart('topByQuantityChart', bar);
            }

            // 4) توزيع الحالات (سليمة/قريبة/منتهية).
            const statusEl = document.getElementById('statusDistributionChart');
            if (statusEl) {
                statusEl.innerHTML = '';
                const dist = d.statusDistribution || {};
                const pie = new window.ApexCharts(statusEl, {
                    chart: { type: 'donut', height: 280 },
                    series: [Number(dist.ok || 0), Number(dist.near || 0), Number(dist.expired || 0)],
                    labels: ['سليمة', 'قريبة الانتهاء', 'منتهية'],
                    colors: [STATUS_COLORS.ok, STATUS_COLORS.near, STATUS_COLORS.expired],
                    legend: { position: 'bottom' },
                    plotOptions: { pie: { donut: { size: '60%' } } },
                    dataLabels: { enabled: true, formatter: (v) => `${Math.round(v)}%` }
                });
                pie.render();
                this.registerChart('statusDistributionChart', pie);
            }
        },

        // ─── Exporting ──────────────────────────────────────────────
        exportExcel() {
            if (!this.canExportPage || !this.activeTabExportable) return;
            const tab = this.activeTabApiName();
            const url = this.$http.GetStockReportExportExcelUrl({
                ...this.apiFilters,
                tab
            });
            window.open(url, '_blank');
        },

        exportPdf() {
            if (!this.canExportPage || !this.activeTabExportable) return;
            const tab = this.activeTabApiName();
            const url = this.$http.GetStockReportExportPdfUrl({
                ...this.apiFilters,
                tab
            });
            window.open(url, '_blank');
        },

        printCurrentTab() {
            this.exportPdf();
        },

        activeTabApiName() {
            switch (this.activeTab) {
                case 'expired': return 'expired';
                case 'expiringSoon': return 'expiringsoon';
                case 'lowStock': return 'lowstock';
                case 'byWarehouse': return 'bywarehouse';
                case 'byCategory': return 'bycategory';
                case 'current':
                default: return 'current';
            }
        },

        // ─── Action shortcuts ────────────────────────────────────────
        openInventoryCount() {
            this.href('/InventoryCounts');
        },
        openProcurementForLowStock() {
            this.href({ path: '/ExternalPurchaseRequests', query: { lowStock: '1' } });
        }
    }
};
